import React, { useEffect, useState } from "react";
import { Modal, Box, Button, CircularProgress } from "@mui/material";
import {
  FaTimes,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaIdCard,
  FaStar,
  FaCoins,
  FaBuilding,
  FaBriefcase,
  FaTools,
  FaClipboardList,
  FaShoppingBag,
  FaHome,
} from "react-icons/fa";
import { usersApi } from "../api/usersApi";
import type { UserAddress } from "../types/users.types";
import "../screenstyles/AccountDetails.css";
import {
  ordersApi,
} from "../api/ordersApi";

import type {
  OrderRecord,
  UserOrdersData,
} from "../types/orders.types";

import {
  accountsApi,
} from "../api/accountsApi";

import type {
  UserAccountApplication,
  UserAccountApplicationData,
} from "../types/accounts.types";
interface DefaultAddressObject {
  phoneNumber?: string;
  location?: {
    latitude?: number;
    longitude?: number;
    latitudeDelta?: number;
    longitudeDelta?: number;
  };
  address?: string;
  City?: string;
  city?: string;
  number?: string;
  street?: string;
  additionalInfo?: string;
  addressId?: string;
  id?: string;
  title?: string;
  nearestPoint?: string;
  gate?: string;
  addressName?: string;
  updatedAt?: any;
province?: string | null;
  locationName?: string;
}
interface AccountData {
  id: string;
  about?: string;
AccountLevel?: string | null;
accountType?: string | null;
  dateOfBirth?: string;
defaultAddress?: string | DefaultAddressObject | null;
email?: string | null;
  fullName?: string;
  gender?: string;
loginType?: string | null;
 phoneNumber?: string | null;
  points?: number;
profileImageUrl?: string | null;
province?: string | null;
  rating?: string;
  uid?: string;
  createdAt?: any;
}

interface OrdersInfo {
  completeCount: number;
  unCompleteCount: number;
}

interface AccountDetailsProps {
  open: boolean;
  onClose: () => void;
  account: AccountData | null;
}

const AccountDetails: React.FC<AccountDetailsProps> = ({
  open,
  onClose,
  account,
}) => {
  const [loadingExtraData, setLoadingExtraData] = useState(false);
const [
  applicationInfo,
  setApplicationInfo,
] =
  useState<UserAccountApplication | null>(
    null,
  );
const [addresses, setAddresses] =
  useState<UserAddress[]>([]);
  const [ordersInfo, setOrdersInfo] = useState<OrdersInfo>({
    completeCount: 0,
    unCompleteCount: 0,
  });

  const isTechnician =
    account?.accountType === "منصب" ||
    account?.accountType === "تقني" ||
    account?.accountType === "حساب منصب";

  const isAgent =
    account?.accountType === "وكيل" ||
    account?.accountType === "حساب وكيل";

  const isNormalUser =
    !isTechnician && !isAgent;

  useEffect(() => {
    if (!open || !account) return;

    const fetchDetailsData = async () => {
      try {
        setLoadingExtraData(true);
        setApplicationInfo(null);
        setAddresses([]);
        setOrdersInfo({
          completeCount: 0,
          unCompleteCount: 0,
        });

        await Promise.all([
          fetchApplicationInfo(),
          fetchUserAddresses(),
          fetchUserOrdersInfo(),
        ]);
      } catch (error) {
        console.error("Error fetching account details:", error);
      } finally {
        setLoadingExtraData(false);
      }
    };

    fetchDetailsData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [open, account?.id]);

const fetchApplicationInfo =
  async () => {
    if (!account?.id) {
      return;
    }

    if (isNormalUser) {
      setApplicationInfo(null);
      return;
    }

    try {
      const result: UserAccountApplicationData =
        await accountsApi.getUserApplication(
          account.id,
        );

      setApplicationInfo(
        result.application,
      );
    } catch (error) {
      console.error(
        "Error fetching account application from API:",
        error,
      );

      setApplicationInfo(null);
    }
  };

const fetchUserAddresses = async () => {
  if (!account?.id) {
    return;
  }

  try {
    const result =
      await usersApi.getAddresses(
        account.id,
      );

    setAddresses(
      result.items,
    );
  } catch (error) {
    console.error(
      "Error fetching user addresses from API:",
      error,
    );

    setAddresses([]);
  }
};

const fetchUserOrdersInfo = async () => {
  if (!account?.id) {
    return;
  }

  try {
    const allOrders: OrderRecord[] = [];

    let cursor: string | undefined =
      undefined;

    let hasMore = true;

    while (hasMore) {
      const result: UserOrdersData =
        await ordersApi.getUserOrders(
          account.id,
          {
            limit: 50,
            sort: "createdAt_desc",
            cursor,
          },
        );

      allOrders.push(
        ...result.orders,
      );

      hasMore =
        result.pagination.hasMore;

      cursor =
        result.pagination.nextCursor ||
        undefined;

      if (
        hasMore &&
        !cursor
      ) {
        break;
      }
    }

    const completeCount =
      allOrders.filter(
        (order) =>
          order.status ===
          "completed",
      ).length;

    const unCompleteCount =
      allOrders.length -
      completeCount;

    setOrdersInfo({
      completeCount,
      unCompleteCount,
    });
  } catch (error) {
    console.error(
      "Error fetching user orders from API:",
      error,
    );

    setOrdersInfo({
      completeCount: 0,
      unCompleteCount: 0,
    });
  }
};

  const formatDate = (value: any) => {
    if (!value) return "غير متوفر";

    try {
      if (value?.toDate) {
        return value.toDate().toLocaleString("ar-IQ", {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      }

      const date = new Date(value);
      if (isNaN(date.getTime())) return "غير متوفر";

      return date.toLocaleString("ar-IQ", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "غير متوفر";
    }
  };
const formatDefaultAddress = (
  value?: string | DefaultAddressObject | null
): string => {
  if (!value) {
    return "غير متوفر";
  }

  // إذا كانت القيمة نصًا أو ID
  if (typeof value === "string") {
    return value.trim() || "غير متوفر";
  }

  // إذا كانت القيمة Object
  const parts = [
    value.addressName,
    value.title,
    value.province,
    value.City,
    value.city,
    value.locationName,
    value.address,
    value.street,
    value.gate,
    value.nearestPoint,
  ].filter(
    (item): item is string =>
      typeof item === "string" && item.trim() !== ""
  );

  if (parts.length > 0) {
    return parts.join(" - ");
  }

  // آخر محاولة: عرض ID العنوان
  return value.addressId || value.id || "عنوان محفوظ";
};
  const showValue = (value?: string | number | null) => {
    if (value === undefined || value === null || value === "") {
      return "غير متوفر";
    }

    return value;
  };

  const DetailItem = ({
    icon,
    label,
    value,
    fullWidth = false,
  }: {
    icon: React.ReactNode;
    label: string;
    value?: string | number | null;
    fullWidth?: boolean;
  }) => (
    <div className={`account-detail-item ${fullWidth ? "full-width" : ""}`}>
      <div className="detail-label-row">
        <span className="detail-icon">{icon}</span>
        <label>{label}</label>
      </div>

      <p>{showValue(value)}</p>
    </div>
  );

  const SectionTitle = ({
    icon,
    title,
    subtitle,
  }: {
    icon: React.ReactNode;
    title: string;
    subtitle?: string;
  }) => (
    <div className="account-section-title">
      <span>{icon}</span>

      <div>
        <h3>{title}</h3>
        {subtitle && <p>{subtitle}</p>}
      </div>
    </div>
  );

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="account-details-modal" dir="rtl">
        {!account ? (
          <div className="account-details-empty">لا توجد بيانات لعرضها</div>
        ) : (
          <>
            <button className="account-details-close" onClick={onClose}>
              <FaTimes />
            </button>

            <div className="account-details-header">
              <div className="account-details-cover" />

              <div className="account-details-user">
                <img
                  src={account.profileImageUrl || "/default-user.png"}
                  alt={account.fullName || "User"}
                  className="account-details-image"
                />

                <div className="account-details-title">
                  <h2>{account.fullName || "بدون اسم"}</h2>

                  <div className="account-header-badges">
                    <span className="account-type-badge">
                      {account.accountType || "نوع الحساب غير محدد"}
                    </span>

                    <span className="account-province-badge">
                      {account.province || "بدون محافظة"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="account-details-body">
              {loadingExtraData && (
                <div className="account-loading-box">
                  <CircularProgress size={24} />
                  <span>جاري تحميل التفاصيل الإضافية...</span>
                </div>
              )}

              {/* معلومات الحساب */}
              <SectionTitle
                icon={<FaUser />}
                title="معلومات الحساب"
                subtitle="البيانات الأساسية"
              />

              <div className="account-details-grid">
                <DetailItem
                  icon={<FaUser />}
                  label="الاسم الكامل"
                  value={account.fullName}
                />

                <DetailItem
                  icon={<FaPhone />}
                  label="رقم الهاتف"
                  value={account.phoneNumber}
                />

                <DetailItem
                  icon={<FaEnvelope />}
                  label="البريد الإلكتروني"
                  value={account.email}
                />

                <DetailItem
                  icon={<FaMapMarkerAlt />}
                  label="المحافظة"
                  value={account.province}
                />

                <DetailItem
                  icon={<FaIdCard />}
                  label="نوع الحساب"
                  value={account.accountType}
                />

                <DetailItem
                  icon={<FaIdCard />}
                  label="مستوى الحساب"
                  value={account.AccountLevel}
                />

                <DetailItem
                  icon={<FaUser />}
                  label="الجنس"
                  value={account.gender}
                />

                <DetailItem
                  icon={<FaCalendarAlt />}
                  label="تاريخ الميلاد"
                  value={account.dateOfBirth}
                />

                <DetailItem
                  icon={<FaStar />}
                  label="التقييم"
                  value={account.rating}
                />

                <DetailItem
                  icon={<FaCoins />}
                  label="النقاط"
                  value={account.points}
                />

                <DetailItem
                  icon={<FaIdCard />}
                  label="نوع تسجيل الدخول"
                  value={account.loginType}
                />

                <DetailItem
                  icon={<FaCalendarAlt />}
                  label="تاريخ إنشاء الحساب"
                  value={formatDate(account.createdAt)}
                />
                

               <DetailItem
  icon={<FaMapMarkerAlt />}
  label="العنوان الافتراضي"
  value={formatDefaultAddress(account.defaultAddress)}
  fullWidth
/>

                <DetailItem
                  icon={<FaUser />}
                  label="نبذة عن الحساب"
                  value={account.about}
                  fullWidth
                />

                <DetailItem
                  icon={<FaIdCard />}
                  label="UID"
                  value={account.uid || account.id}
                  fullWidth
                />
              </div>

              {/* معلومات التقديم */}
              <SectionTitle
                icon={<FaClipboardList />}
                title="معلومات التقديم"
                subtitle={
                  isNormalUser
                    ? "هذا الحساب مستخدم عادي، لذلك لا توجد بيانات تقديم"
                    : isTechnician
                    ? "البيانات مجلوبة من requestfortechnicians"
                    : "البيانات مجلوبة من requestforagent"
                }
              />

              {isNormalUser ? (
                <div className="account-empty-section">
                  لا توجد معلومات تقديم لأن نوع الحساب مستخدم.
                </div>
              ) : !applicationInfo ? (
                <div className="account-empty-section">
                  لا توجد معلومات تقديم لهذا الحساب.
                </div>
              ) : (
                <div className="account-details-grid">
                  <DetailItem
                    icon={<FaBuilding />}
                    label="اسم الشركة"
                    value={applicationInfo.companyName}
                  />

                  <DetailItem
                    icon={<FaUser />}
                    label="الاسم في الطلب"
                    value={applicationInfo.fullName}
                  />

                  <DetailItem
                    icon={<FaPhone />}
                    label="رقم الهاتف في الطلب"
                    value={applicationInfo.phoneNumber}
                  />

                  <DetailItem
                    icon={<FaEnvelope />}
                    label="الإيميل في الطلب"
                    value={applicationInfo.emailAddress}
                  />

                  <DetailItem
                    icon={<FaBriefcase />}
                    label="الخبرة / الاختصاص"
                    value={applicationInfo.expertise}
                  />

                  <DetailItem
                    icon={<FaCalendarAlt />}
                    label="سنوات الخبرة"
                    value={applicationInfo.years}
                  />

                  <DetailItem
                    icon={<FaTools />}
                    label="الخدمات"
                    value={applicationInfo.services}
                  />

                  <DetailItem
                    icon={<FaMapMarkerAlt />}
                    label="المناطق التي يغطيها"
                    value={applicationInfo.regions}
                  />

                  <DetailItem
                    icon={<FaMapMarkerAlt />}
                    label="العنوان الكامل"
                    value={applicationInfo.fullAddress}
                    fullWidth
                  />

                  <DetailItem
                    icon={<FaBuilding />}
                    label="الشراكات"
                    value={applicationInfo.partnerships}
                    fullWidth
                  />

                  <DetailItem
                    icon={<FaCalendarAlt />}
                    label="تاريخ التقديم"
                    value={formatDate(applicationInfo.createdAt)}
                  />

                  <DetailItem
                    icon={<FaCalendarAlt />}
                    label="آخر تحديث للطلب"
                    value={formatDate(applicationInfo.updatedAt)}
                  />
                </div>
              )}

              {/* معلومات الطلبات */}
              <SectionTitle
                icon={<FaShoppingBag />}
                title="معلومات الطلبات"
                subtitle="عدد الطلبات المكتملة وغير المكتملة لهذا الحساب"
              />

              <div className="account-summary-cards">
                <div className="summary-card">
                  <span>الطلبات المكتملة</span>
                  <strong>{ordersInfo.completeCount}</strong>
                </div>

                <div className="summary-card">
                  <span>الطلبات غير المكتملة</span>
                  <strong>{ordersInfo.unCompleteCount}</strong>
                </div>
              </div>

              {/* معلومات العناوين */}
              <SectionTitle
                icon={<FaHome />}
                title="معلومات العناوين"
                subtitle="العناوين المحفوظة"
              />

              {addresses.length === 0 ? (
                <div className="account-empty-section">
                  لا توجد عناوين محفوظة لهذا الحساب.
                </div>
              ) : (
                <div className="addresses-list">
                  {addresses.map((address, index) => (
                    <div key={address.addressId} className="address-card">
                      <div className="address-card-header">
                        <strong>العنوان رقم {index + 1}</strong>
                        <span>{address.province || "بدون محافظة"}</span>
                      </div>

                      <div className="address-card-body">
                        <p>
                          <b>اسم الموقع:</b>{" "}
                          {address.locationName || "غير متوفر"}
                        </p>

                     <p>
  <b>المدينة:</b>{" "}
  {address.City || "غير متوفر"}
</p>

                        <p>
                          <b>البوابة:</b> {address.gate || "غير متوفر"}
                        </p>

                        <p>
                          <b>رقم الهاتف:</b>{" "}
                          {address.phoneNumber || "غير متوفر"}
                        </p>

                        <p>
                          <b>معلومات إضافية:</b>{" "}
                          {address.additionalInfo || "غير متوفر"}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* معلومات النقاط */}
              {(isTechnician || isAgent) && (
                <>
                  <SectionTitle
                    icon={<FaCoins />}
                    title="معلومات النقاط"
                    subtitle="تظهر هذه المعلومات فقط للمنصب أو الوكيل"
                  />

                  <div className="account-summary-cards">
                    <div className="summary-card points-card">
                      <span>إجمالي النقاط الحالية</span>
                      <strong>{account.points || 0}</strong>
                    </div>

                    <div className="summary-card">
                      <span>نوع الحساب</span>
                      <strong>{account.accountType || "غير متوفر"}</strong>
                    </div>
                  </div>
                </>
              )}

              <div className="account-details-actions">
                <Button
                  variant="contained"
                  onClick={onClose}
                  sx={{
                    backgroundColor: "#262d34",
                    color: "#fff",
                    fontFamily: "Tajawal, sans-serif",
                    borderRadius: "12px",
                    px: 5,
                    py: 1,
                    "&:hover": {
                      backgroundColor: "#1b2025",
                    },
                  }}
                >
                  إغلاق
                </Button>
              </div>
            </div>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default AccountDetails;